import useMediaQuery from '@mui/material/useMediaQuery';
import React, {createContext, Dispatch, SetStateAction, useMemo} from 'react';
import {useSearchParam} from 'react-use';
import {FrameStoreBuffer} from '../types';
import {Theme} from '@mui/material';

interface TourUIContextValue {
  drawerRef: React.RefObject<HTMLDivElement>;
  tourCanvasRef: React.RefObject<HTMLCanvasElement>;
  buffer: FrameStoreBuffer;
  setBuffer: Dispatch<SetStateAction<FrameStoreBuffer>>;
  currentIndex: number;
  setCurrentIndex: Dispatch<SetStateAction<number>>;
  drawerIsOpen: boolean;
  setDrawerIsOpen: Dispatch<SetStateAction<boolean>>;
  keepDrawerOpen: boolean;
  setKeepDrawerOpen: Dispatch<SetStateAction<boolean>>;
  isMobile: boolean;
  fastTravellingStatus: 'idle' | 'forward' | 'backward';
  setFastTravellingStatus: React.Dispatch<
    React.SetStateAction<'idle' | 'forward' | 'backward'>
  >;
  params: {
    disableControls?: boolean;
    liveViewing?: boolean;
  };
}

const TourUIContext = createContext<TourUIContextValue | null>(null);
export const useTourUIContext = () => {
  const context = React.useContext(TourUIContext);
  if (!context) {
    throw new Error('useTourUIContext should have a TourUIContextProvider');
  }
  return context;
};

export function TourUIContextProvider({children}: {children: React.ReactNode}) {
  const disabledControls = useSearchParam('disabledControls');
  const liveViewing = useSearchParam('liveViewing');
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const tourCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const splitDisableControls = disabledControls?.toLowerCase()?.split(',');
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md')
  );
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [buffer, setBuffer] = React.useState<FrameStoreBuffer>({
    totalImages: 0,
    storeLength: 0,
    fps: 0,
  });
  const [drawerIsOpen, setDrawerIsOpen] = React.useState(false);
  const [keepDrawerOpen, setKeepDrawerOpen] = React.useState(false);
  const [fastTravellingStatus, setFastTravellingStatus] = React.useState<
    'idle' | 'forward' | 'backward'
  >('idle');

  return (
    <TourUIContext.Provider
      value={useMemo(
        () => ({
          drawerRef,
          tourCanvasRef,
          buffer,
          setBuffer,
          currentIndex,
          setCurrentIndex,
          drawerIsOpen,
          setDrawerIsOpen,
          keepDrawerOpen,
          setKeepDrawerOpen,
          fastTravellingStatus,
          setFastTravellingStatus,
          isMobile,
          params: {},
        }),
        [
          buffer,
          currentIndex,
          drawerIsOpen,
          fastTravellingStatus,
          isMobile,
          keepDrawerOpen,
        ]
      )}
    >
      {children}
    </TourUIContext.Provider>
  );
}
